import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import wedding from '../assets/home/wedding5.jpg';

const variants = {
  offer: {
    eyebrow: 'Commission Enquiry Received',
    heading: 'Your Date Is Being Checked',
    lead: "Thank you — we've received your £999 commission enquiry and we're confirming availability for your date now.",
    responseTime: 'Within 24 hours',
    steps: [
      {
        title: 'We Check Your Date',
        body: 'We confirm your wedding date is still open in our calendar and hold it while we speak.',
      },
      {
        title: 'We Email You',
        body: 'You will receive a reply within 24 hours confirming availability and next steps.',
      },
      {
        title: 'We Secure The Rate',
        body: 'Once confirmed, your £999 rate is locked in and your date comes off the market.',
      },
    ],
  },
  contact: {
    eyebrow: 'Inquiry Received',
    heading: 'Thank You For Reaching Out',
    lead: "Your brief has landed with us. We read every inquiry personally and will be in touch shortly.",
    responseTime: 'Within 48 hours',
    steps: [
      {
        title: 'We Read Your Brief',
        body: 'Your project details go straight to our team — no automated triage, no queue.',
      },
      {
        title: 'Consultation Call',
        body: 'We schedule a call to align on aesthetic vision, logistics, and creative direction.',
      },
      {
        title: 'Tailored Proposal',
        body: 'You receive a bespoke proposal with transparent pricing and deliverable timelines.',
      },
    ],
  },
};

const ThankYou = () => {
  const { state } = useLocation();
  const content = variants[state?.source];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Reachable only via a successful form submission, which supplies `source`.
  if (!content) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <Helmet>
        <title>Thank You | Glintz Photography</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <Navbar />

      <section className="relative min-h-screen overflow-hidden bg-black flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${wedding})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/85 to-black/70 z-0" />

        {/* Stays below the fixed navbar (z-20) — this block spans the full hero and would otherwise eat its clicks. */}
        <div className="relative z-10 w-full xl:px-44 lg:px-24 px-6 py-32 xl:py-40">
          <div className="max-w-3xl">
            <p
              className="text-white/70 uppercase tracking-[.4em] text-xs mb-6"
              style={{ fontFamily: 'Muli, sans-serif' }}
            >
              {content.eyebrow}
            </p>
            <h1 className="text-white uppercase xl:text-5xl lg:text-4xl md:text-3xl text-2xl leading-[1.1] tracking-[.08em] mb-8">
              {content.heading}
            </h1>
            <div className="w-12 h-px bg-white/50 mb-8" />
            <p
              className="text-white/80 text-sm md:text-base leading-relaxed mb-12 max-w-xl"
              style={{ fontFamily: 'Muli, sans-serif' }}
            >
              {content.lead}
            </p>

            {/* Response time */}
            <div className="inline-block border border-white/30 px-8 py-6 mb-16">
              <p
                className="text-white/70 uppercase tracking-[.3em] text-[10px] mb-1"
                style={{ fontFamily: 'Muli, sans-serif' }}
              >
                You'll Hear From Us
              </p>
              <p className="text-white text-2xl font-light tracking-tight">
                {content.responseTime}
              </p>
            </div>

            {/* What happens next */}
            <div className="grid md:grid-cols-3 grid-cols-1 gap-10 mb-16">
              {content.steps.map((step, i) => (
                <div key={i}>
                  <p
                    className="text-white/50 text-xs tracking-[.2em] uppercase mb-3"
                    style={{ fontFamily: 'Muli, sans-serif' }}
                  >
                    0{i + 1}
                  </p>
                  <h2 className="text-white uppercase tracking-[.1em] text-sm font-semibold mb-3">
                    {step.title}
                  </h2>
                  <p
                    className="text-white/70 text-sm leading-relaxed"
                    style={{ fontFamily: 'Muli, sans-serif' }}
                  >
                    {step.body}
                  </p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/portfolio"
                className="inline-block bg-white text-black px-10 py-4 text-xs tracking-[.25em] uppercase hover:bg-white/90 transition-all"
                style={{ fontFamily: 'Muli, sans-serif' }}
              >
                Explore Our Work
              </Link>
              <Link
                to="/"
                className="inline-block border border-white/40 text-white px-10 py-4 text-xs tracking-[.25em] uppercase hover:bg-white hover:text-black transition-all"
                style={{ fontFamily: 'Muli, sans-serif' }}
              >
                Back To Home
              </Link>
            </div>

            <p
              className="text-white/50 text-xs leading-relaxed mt-12"
              style={{ fontFamily: 'Muli, sans-serif' }}
            >
              Need us sooner? Email{' '}
              <a
                href="mailto:temitope@glintzphotography.org"
                className="text-white/80 hover:text-white underline underline-offset-4"
              >
                temitope@glintzphotography.org
              </a>{' '}
              or call{' '}
              <a
                href="tel:+447823683844"
                className="text-white/80 hover:text-white underline underline-offset-4"
              >
                +44 782 368 3844
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ThankYou;
